import React, { useState, useEffect } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  LayoutDashboard,
  BarChart3,
  Users,
  FileText,
  Search,
  Bell,
  Settings,
  Menu,
  X,
  Stethoscope,
  Activity,
  ChevronRight,
  LogOut,
  User
} from 'lucide-react';
import { ROUTE_PATHS } from '@/lib/index';
import { IMAGES } from '@/assets/images';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { cn } from '@/lib/utils';

interface LayoutProps {
  children: React.ReactNode;
}

const navigation = [
  { name: 'Dashboard', href: ROUTE_PATHS.DASHBOARD, icon: LayoutDashboard },
  { name: 'Analytics', href: ROUTE_PATHS.ANALYTICS, icon: BarChart3 },
  { name: 'Patients', href: ROUTE_PATHS.PATIENTS, icon: Users },
  { name: 'Reports', href: ROUTE_PATHS.REPORTS, icon: FileText },
];

export function Layout({ children }: LayoutProps) {
  const [isSidebarOpen, setIsSidebarOpen] = useState(true);
  const [isScrolled, setIsScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 10);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <div className="relative min-h-screen bg-background text-foreground font-sans selection:bg-primary/20 selection:text-primary">
      {/* Background Layer with Medical Theme */}
      <div className="fixed inset-0 z-0 overflow-hidden pointer-events-none">
        <img
          src={IMAGES.MEDICAL_BG_7}
          alt="Medical Background"
          className="absolute inset-0 object-cover w-full h-full opacity-10 scale-110 blur-[100px] animate-pulse"
        />
        <div className="absolute inset-0 bg-gradient-to-br from-background/95 via-background/80 to-background/95" />
        <div className="absolute top-0 right-0 w-[500px] h-[500px] bg-primary/5 rounded-full blur-[120px] -translate-y-1/2 translate-x-1/3" />
        <div className="absolute bottom-0 left-0 w-[400px] h-[400px] bg-accent/5 rounded-full blur-[100px] translate-y-1/3 -translate-x-1/4" />
      </div>

      {/* Sidebar */}
      <aside
        className={cn(
          "fixed left-0 top-0 z-40 h-screen transition-all duration-300 ease-in-out border-r border-border/40 bg-card/40 backdrop-blur-xl",
          isSidebarOpen ? "w-64" : "w-20"
        )}
      >
        <div className="flex flex-col h-full">
          {/* Logo Section */}
          <div className="flex items-center gap-3 px-6 h-16 border-b border-border/40">
            <div className="flex items-center justify-center w-10 h-10 rounded-xl bg-primary text-primary-foreground shadow-lg shadow-primary/20">
              <Stethoscope className="w-6 h-6" />
            </div>
            {isSidebarOpen && (
              <motion.span
                initial={{ opacity: 0, x: -10 }}
                animate={{ opacity: 1, x: 0 }}
                className="text-lg font-bold tracking-tight text-foreground"
              >
                MediPulse <span className="text-primary text-[10px] align-top font-mono">v2.0</span>
              </motion.span>
            )}
          </div>

          {/* Navigation Items */}
          <nav className="flex-1 px-3 py-6 space-y-1.5 overflow-y-auto">
            {navigation.map((item) => {
              const isActive = location.pathname === item.href;
              return (
                <NavLink
                  key={item.name}
                  to={item.href}
                  className={({ isActive }) =>
                    cn(
                      "group relative flex items-center gap-3 px-3 py-3 rounded-xl transition-all duration-200",
                      isActive
                        ? "bg-primary/10 text-primary shadow-[inset_0_0_12px_rgba(var(--primary),0.05)]"
                        : "text-muted-foreground hover:bg-secondary hover:text-foreground"
                    )
                  }
                >
                  <item.icon className={cn("w-5 h-5 shrink-0", isActive ? "text-primary" : "group-hover:text-foreground")} />
                  {isSidebarOpen && (
                    <motion.span
                      initial={{ opacity: 0, x: -5 }}
                      animate={{ opacity: 1, x: 0 }}
                      className="font-medium whitespace-nowrap"
                    >
                      {item.name}
                    </motion.span>
                  )}
                  {isActive && (
                    <motion.div
                      layoutId="active-nav"
                      className="absolute left-0 w-1 h-6 bg-primary rounded-r-full"
                      transition={{ type: "spring", stiffness: 300, damping: 30 }}
                    />
                  )}
                </NavLink>
              );
            })}
          </nav>

          {/* Footer Sidebar Items */}
          <div className="p-3 mt-auto space-y-1.5 border-t border-border/40">
            <button
              onClick={() => setIsSidebarOpen(!isSidebarOpen)}
              className="flex items-center w-full gap-3 px-3 py-3 rounded-xl text-muted-foreground hover:bg-secondary hover:text-foreground transition-all"
            >
              <Menu className={cn("w-5 h-5 shrink-0", !isSidebarOpen && "mx-auto")} />
              {isSidebarOpen && <span className="font-medium">Collapse View</span>}
            </button>
          </div>
        </div>
      </aside>

      {/* Main Content Area */}
      <main
        className={cn(
          "relative z-10 min-h-screen transition-all duration-300 ease-in-out",
          isSidebarOpen ? "pl-64" : "pl-20"
        )}
      >
        {/* Header */}
        <header
          className={cn(
            "sticky top-0 z-30 flex items-center justify-between h-16 px-8 transition-all duration-300 border-b border-transparent",
            isScrolled ? "bg-background/80 backdrop-blur-md border-border/40 shadow-sm" : "bg-transparent"
          )}
        >
          <div className="flex items-center flex-1 max-w-xl">
            <div className="relative w-full group">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground group-focus-within:text-primary transition-colors" />
              <Input
                type="search"
                placeholder="Search patient records, reports, analytics..."
                className="w-full pl-10 h-10 bg-card/30 border-border/40 focus:bg-card/60 rounded-xl transition-all"
              />
            </div>
          </div>

          <div className="flex items-center gap-4">
            <div className="hidden md:flex items-center gap-2 mr-4 px-3 py-1.5 bg-accent/10 rounded-full border border-accent/20">
              <Activity className="w-4 h-4 text-accent animate-pulse" />
              <span className="text-xs font-semibold text-accent uppercase tracking-wider">System Live</span>
            </div>

            <Button variant="ghost" size="icon" className="relative rounded-full hover:bg-secondary">
              <Bell className="w-5 h-5 text-muted-foreground" />
              <span className="absolute top-2 right-2 w-2 h-2 bg-destructive rounded-full border-2 border-background" />
            </Button>

            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" className="flex items-center gap-2 p-1.5 rounded-full hover:bg-secondary transition-all">
                  <Avatar className="w-8 h-8 border border-border/60">
                    <AvatarImage src="https://github.com/shadcn.png" alt="Dr. Sarah Chen" />
                    <AvatarFallback>SC</AvatarFallback>
                  </Avatar>
                  <div className="hidden lg:block text-left mr-2">
                    <p className="text-xs font-bold leading-none">Dr. Sarah Chen</p>
                    <p className="text-[10px] text-muted-foreground mt-0.5">Chief Medical Officer</p>
                  </div>
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56 rounded-xl p-2">
                <DropdownMenuLabel>My Account</DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem className="rounded-lg gap-2 cursor-pointer">
                  <User className="w-4 h-4" /> Profile
                </DropdownMenuItem>
                <DropdownMenuItem className="rounded-lg gap-2 cursor-pointer">
                  <Settings className="w-4 h-4" /> Settings
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem className="rounded-lg gap-2 cursor-pointer text-destructive focus:bg-destructive/10">
                  <LogOut className="w-4 h-4" /> Sign out
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </header>

        {/* Page Content */}
        <div className="px-8 py-8">
          <AnimatePresence mode="wait">
            <motion.div
              key={location.pathname}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              transition={{ duration: 0.2, ease: "easeOut" }}
            >
              {children}
            </motion.div>
          </AnimatePresence>
        </div>

        {/* Global Footer (Dashboard Style) */}
        <footer className="mt-auto px-8 py-6 border-t border-border/20 flex flex-col md:flex-row items-center justify-between gap-4 text-xs text-muted-foreground">
          <div className="flex items-center gap-6">
            <p>© 2026 MediPulse Analytics. All rights reserved.</p>
            <div className="flex items-center gap-4">
              <a href="#" className="hover:text-primary transition-colors">Privacy Policy</a>
              <a href="#" className="hover:text-primary transition-colors">Terms of Service</a>
              <a href="#" className="hover:text-primary transition-colors">Help Center</a>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <span className="flex h-2 w-2 rounded-full bg-emerald-500" />
            <span className="font-mono uppercase tracking-widest opacity-60">System Operational</span>
          </div>
        </footer>
      </main>

      {/* 3D Decorative Element - Floating DNA / Medical Molecule */}
      <div className="fixed bottom-10 right-10 z-50 pointer-events-none opacity-20 hidden xl:block">
        <motion.div
          animate={{
            y: [0, -20, 0],
            rotate: [0, 5, -5, 0]
          }}
          transition={{
            duration: 8,
            repeat: Infinity,
            ease: "easeInOut"
          }}
        >
          <img 
            src={IMAGES.MEDICAL_TECH_1} 
            alt="Medical Tech" 
            className="w-48 h-48 object-contain filter drop-shadow-[0_0_20px_rgba(var(--primary),0.3)]"
          />
        </motion.div>
      </div>
    </div>
  );
}
